<template>
	<v-card v-if="appointment && appointment.id" elevation="0" class="confirmation">
		<v-card-text class="pa-0">
			<div>
				<div class="schedule-header">
					<div class="icon">
						<img :src="LsContactScheduleConfirmationSvg" alt="Scheduled" />
					</div>
					<h3 class="schedule-title">Confirmed</h3>
					<h6 class="schedule-subtitle">
						You are scheduled with
						<i v-if="!name && !phone">(contact deleted)</i>
						<template v-else>{{ name || phone }}</template>
					</h6>
					<!-- LSP-16658: `originalStartAt` is set once, on the FIRST reschedule, and never
					     overwritten — so its presence is the "this call has been moved" flag as well as the
					     time it was moved from. Rendered in the appointment's own timezone and the org's
					     date format, exactly like the scheduled time above it. -->
					<div v-if="rescheduledFromDisplay" data-test-id="rescheduled-from-badge" class="schedule-rescheduled-badge">
						Rescheduled from {{ rescheduledFromDisplay }}
					</div>
				</div>
				<hr />
				<!-- LSP-20787: the invite's delivery banner (SmsDeliveryBanner.vue); absent unless the session page passes a state. -->
				<SmsDeliveryBanner
					v-if="delivery"
					:delivery="delivery"
					:conversation="conversation"
					:call-when="callWhen"
					:can-repair="canRepairDelivery"
					:email-invitations-allowed="emailInvitationsAllowed"
					@resent="$emit('delivery-resent', $event)"
					@refresh="$emit('delivery-refresh')"
					@customer-updated="$emit('delivery-customer-updated', $event)" />
				<v-container class="schedule py-0">
					<v-row density="compact" class="time">
						<v-col cols="auto" class="d-flex align-center">
							<v-icon color="primary">icon-calendar-check-stroke</v-icon>
						</v-col>
						<v-col class="d-flex align-center">
							<span>{{ scheduledDisplayTime }}</span>
						</v-col>
					</v-row>
					<v-row density="compact" class="timezone">
						<v-col cols="auto" class="d-flex align-center">
							<v-icon color="primary">icon-globe-website-stroke</v-icon>
						</v-col>
						<v-col class="d-flex align-center">
							<span>{{ timeZoneDisplay }}</span>
						</v-col>
					</v-row>
				</v-container>
				<hr />
				<div class="calendar-links">
					<div class="link-container">
						<a class="link" :href="appointment.googleCalendarUrl" target="_blank">
							<img :src="LsContactGoogleLogoPng" alt="Add to Google Calendar" />
							<span>Add to Google Calendar</span>
						</a>
						<a class="link" :href="icsText" :download="downloadName" @click="showDownloadDialog('Apple')">
							<img :src="LsContactAppleLogoSvg" alt="Add to Apple Calendar" />
							<span>Add to Apple Calendar</span>
						</a>
						<a class="link" :href="icsText" :download="downloadName" @click="showDownloadDialog('Outlook')">
							<img :src="LsContactOutlookLogoSvg" alt="Add to Outlook Calendar" />
							<span>Add to Outlook Calendar</span>
						</a>
					</div>
				</div>
			</div>
		</v-card-text>
		<v-card-actions class="flex-column ga-2">
			<v-spacer />

			<!-- LSP-16658: Reschedule and Join share one row; Cancel Call keeps its own full-width row
			     below, unchanged. Reschedule is the secondary action (outlined) so Join stays the
			     obvious primary one. -->
			<div v-if="conversation" class="appointment-action-row d-flex ga-2 w-100">
				<!--Reschedule-->
				<v-btn
					v-if="showReschedule"
					data-test-id="reschedule-scheduled-call"
					prepend-icon="icon-arrow-left-right"
					rounded="pill"
					color="primary"
					variant="outlined"
					@click="$emit('reschedule-call')"
					>Reschedule</v-btn
				>

				<!--Join-->
				<v-btn prepend-icon="icon-video" rounded="pill" color="primary" variant="elevated" elevation="1" @click="join()"
					>Join</v-btn
				>
			</div>

			<!--Cancel Call-->
			<DangerBtn v-if="showCancelCall" data-test-id="cancel-scheduled-call" block @click="$emit('cancel-call')"
				>Cancel Call</DangerBtn
			>
		</v-card-actions>
	</v-card>
	<v-card v-else elevation="0" class="confirmation">
		<v-card-text class="d-flex align-center justify-center">
			<h4>No appointment scheduled</h4>
		</v-card-text>
	</v-card>
</template>
<script>
	import moment from "moment-timezone";
	import { getDateTokens } from "../helpers/dateFormat";
	import timezones from "../helpers/timezones";
	import LsContactScheduleConfirmationSvg from "@/assets/ls-contact-schedule-confirmation.svg";
	// LSP-18630: the legacy four-segment Google mark was retired; this is the current gradient
	// "super G" derived from Google's official signin-assets bundle.
	import LsContactGoogleLogoPng from "@/assets/google-g.png";
	import LsContactAppleLogoSvg from "@/assets/ls-contact-apple-logo.svg";
	import LsContactOutlookLogoSvg from "@/assets/ls-contact-outlook-logo.svg";
	import { useToast } from "vue-toastification";
	import { calculateTimeout } from "@/helpers/utilities";
	import SmsDeliveryBanner from "@/components/SmsDeliveryBanner.vue";

	export default {
		components: { SmsDeliveryBanner },
		props: {
			appointment: Object,
			conversation: Object,
			name: String,
			phone: String,
			timeZone: String,
			// LSP-16746: opt-in so the just-scheduled Confirmed card in ActionSchedule.vue (which
			// doesn't handle the cancel-call emit) doesn't grow a dead button — only
			// EntityDetailPage's scheduled-call card sets this.
			showCancelCall: { type: Boolean, default: false },
			// LSP-16658: same opt-in shape as showCancelCall, and for the same reason — the
			// just-scheduled Confirmed card in ActionSchedule.vue does not handle the reschedule-call
			// emit, so without this it would grow a button that does nothing.
			showReschedule: { type: Boolean, default: false },
			// LSP-20787: absent (null) unless the session page has a delivery state to show — the
			// just-scheduled Confirmed card in ActionSchedule.vue never passes one.
			delivery: { type: Object, default: null },
			canRepairDelivery: { type: Boolean, default: false },
			// Final review I1: the organization's "Allow Email Invitations"; off hides the banner's Send via email.
			emailInvitationsAllowed: { type: Boolean, default: true }
		},
		emits: ["cancel-call", "reschedule-call", "delivery-resent", "delivery-refresh", "delivery-customer-updated"],
		setup() {
			const toast = useToast();
			return {
				toast,
				LsContactScheduleConfirmationSvg,
				LsContactGoogleLogoPng,
				LsContactAppleLogoSvg,
				LsContactOutlookLogoSvg
			};
		},
		data() {
			return {
				downloadName: "event.ics"
			};
		},
		computed: {
			scheduledDisplayTime() {
				if (!this.appointment || !this.appointment.id) {
					return "";
				}

				const start = moment(this.appointment.startAt).tz(this.timeZone);
				const end = moment(this.appointment.endAt).tz(this.timeZone);
				const display = `${start.format("h:mma")} - ${end.format("h:mma")}, ${start.format(getDateTokens(timezones.getDateFormat()).weekdayMonthDayNoYear)}`;
				return display;
			},
			// LSP-16658: the pre-move start, formatted the same way scheduledDisplayTime formats the
			// current one — same timezone, same org date tokens — so the two read as one before/after
			// pair rather than as two unrelated dates. Empty string when the call has never been moved,
			// which is also what hides the badge.
			rescheduledFromDisplay() {
				const originalStartAt = this.appointment?.originalStartAt;
				if (!originalStartAt) {
					return "";
				}
				const originalStart = moment(originalStartAt).tz(this.timeZone);
				if (!originalStart.isValid()) {
					// Diagnostic rather than a silent empty badge: a malformed originalStartAt means the
					// server wrote something unparseable, and "the badge just never appeared" is exactly the
					// report that would otherwise need reconstructing from indirect signals.
					console.warn("AppointmentConfirmation: originalStartAt is not a parseable date — hiding the rescheduled badge", {
						appointmentId: this.appointment?.id,
						originalStartAt
					});
					return "";
				}
				const tokens = getDateTokens(timezones.getDateFormat());
				return `${originalStart.format(tokens.weekdayMonthDayNoYear)} · ${originalStart.format("h:mma")}`;
			},
			icsText() {
				if (!this.appointment || !this.appointment.id) {
					return "";
				}

				let icsText = `data:text/calendar;charset=utf-8,${encodeURIComponent(this.appointment.icsText)}`;
				return icsText;
			},
			timeZoneAbbreviation() {
				const abbr = moment(this.appointment.startAt).tz(this.timeZone).format("z");
				return /[a-zA-Z]/.test(abbr) ? abbr : `GMT${abbr}`;
			},
			timeZoneDisplay() {
				if (!this.timeZone) {
					return "";
				}

				const abbr = this.timeZoneAbbreviation;
				// LSP-21865: underscores become spaces ("America/New York"), the spelling the picker and the
				// Reschedule dialog use — stripping them read as "America/NewYork".
				return `${this.timeZone.replaceAll("_", " ")} - ${abbr}`;
			},
			// LSP-20787 (§5): the banner names the call in the same tokens and zone as the "Rescheduled from" badge above.
			// LSP-21861: and says which zone, so it can be read against the session title's time.
			callWhen() {
				if (!this.appointment?.startAt) return "";
				const start = moment(this.appointment.startAt).tz(this.timeZone);
				return `${start.format(getDateTokens(timezones.getDateFormat()).weekdayMonthDayNoYear)} · ${start.format("h:mma")} ${this.timeZoneAbbreviation}`;
			}
		},
		mounted() {},
		methods: {
			join() {
				// LSP-15319: both fail conditions on this path used to be silent — the button
				// appeared to do nothing and the user stayed on the confirmation card. Log each
				// one separately so a future report names the branch instead of requiring it to
				// be reconstructed. Mirrors ActionSchedule.join(), the same action on the other
				// surface, which has been guarded since the original report.
				const creatorUrl = this.conversation?.creatorUrl;
				if (!creatorUrl) {
					console.warn("AppointmentConfirmation.join: no creatorUrl on the conversation — nothing to open", {
						conversationId: this.conversation?.id,
						appointmentId: this.appointment?.id
					});
					const missingUrlMessage = "This call isn't ready to join yet. Please refresh the page and try again.";
					this.toast.error(missingUrlMessage, { timeout: calculateTimeout(missingUrlMessage) });
					return;
				}

				const win = window.open(creatorUrl, "_blank");
				if (!win) {
					// A blocked popup and an exhausted tab limit (iOS Safari caps around 500) are
					// not programmatically distinguishable and manifest identically, so one
					// null-check covers both — see the ticket's "Note on detection".
					console.warn("AppointmentConfirmation.join: window.open returned null — popup blocked or tab limit reached", {
						conversationId: this.conversation?.id,
						appointmentId: this.appointment?.id
					});
					const message =
						"Unable to open a new window. Please check your popup blocker settings or close some tabs and try again.";
					this.toast.error(message, { timeout: calculateTimeout(message) });
				}
			},
			showDownloadDialog(calendarName) {
				this.toast.info(
					`To finish adding this video call to your ${calendarName} Calendar, please locate and open the '${this.downloadName}' file that was downloaded to your device or use your calendar's import feature.`,
					{ timeout: 30000 }
				);
			}
		}
	};
</script>
<style scoped>
	.confirmation .schedule-header {
		text-align: center;
	}

	.confirmation hr {
		margin: 16px 0;
		color: #dfe0e2;
	}

	.confirmation .schedule-title {
		margin: 12px 0;
		background: linear-gradient(93deg, #019c9c 0%, #01bcbc 27.62%);
		background-clip: text;
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
	}

	.confirmation {
		color: rgb(var(--v-theme-ink));
		font-size: 1em;
	}

	.confirmation .time,
	.confirmation .timezone {
		line-height: 1.5;
	}

	.confirmation .schedule {
		color: #9b9ea5;
	}

	.confirmation .schedule .icon {
		color: rgb(var(--v-theme-brand));
	}

	.confirmation .meeting-link {
		font-size: 1rem;
		padding: 0 24px;
		margin-bottom: 16px;
		text-align: center;
	}

	.confirmation .calendar-links {
		font-size: 0.875em;
		font-weight: 600;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
	}

	.confirmation .calendar-links .link {
		display: flex;
		margin-bottom: 16px;
		align-items: center;
		align-self: stretch;
		color: rgba(var(--v-theme-ink), 0.467);
	}

	.confirmation .calendar-links .link img {
		/* LSP-18630: size these explicitly instead of leaning on each asset's intrinsic size.
		   The Apple and Outlook marks are 21x21 SVGs, but the Google mark is now a 160px raster,
		   so intrinsic sizing rendered it roughly 8x too large next to its siblings. */
		width: 21px;
		height: 21px;
		margin-right: 20px;
	}

	.confirmation .calendar-links .link:nth-child(3) {
		margin-bottom: 0px;
	}

	/* LSP-16658: Reschedule and Join split the row evenly. `flex: 1 1 0` (not flex-grow alone) so the
	   two are the same width regardless of label length, and so Join still fills the row on the card
	   that does not opt into Reschedule. No Vuetify utility expresses a zero flex-basis. */
	.confirmation .appointment-action-row > .v-btn {
		flex: 1 1 0;
		min-width: 0;
	}

	.confirmation .schedule-rescheduled-badge {
		margin-top: 8px;
		font-size: 0.8125rem;
		color: #6b6b76;
	}
</style>
